<template>
  <SrpModal :isVisible="props.isVisible" @update:isVisible="emit('update:isVisible', $event)" size="medium" :zIndex="1200" isWithScroll :isClosable="false">
    <template #header>
      <div class="location-editor__header">
        <h2 class="global-h2">{{ props.location ? "Edit Account/Location" : "Add Account/Location" }}</h2>
        <p>Search for a location to auto-fill its social accounts, or manually enter.</p>
      </div>
    </template>
    <template #content>
      <div class="location-editor">
        <div v-if="!selectedPlace" class="location-editor__field">
          <GMapAutocomplete
            ref="placeSearchInput"
            class="global-text-input"
            placeholder="Search for a business or place"
            :options="{ fields: ['name', 'place_id', 'address_components', 'formatted_address', 'geometry', 'website'] }"
            @place_changed="onGooglePlaceSelected"
            @error="onAutocompleteError"
          />
          <LinkWithIcon v-if="!showDetailFields" color="gray" isDottedUnderline @click="isManualMode = true" style="margin-top: 8px">
            <template #icon><IconEmbedded name="edit-pencil_2-5" :size="17" /></template>
            <span>Manually Enter</span>
          </LinkWithIcon>
        </div>

        <template v-if="showDetailFields">
          <div class="location-editor__identity">
            <div class="location-editor__avatar-wrap">
              <LocationAvatar :src="previewIcon" :name="name" :size="56" placeholderTone="gray" />
              <button v-if="isNameEditMode && previewIcon" type="button" class="location-editor__clear-icon" aria-label="Clear location icon" @mousedown.prevent @click="clearIcon">
                <IconEmbedded name="remove_2" :size="12" color="#667085" />
              </button>
            </div>
            <div class="location-editor__identity-body">
              <div v-if="name.trim() && !isEditingName" class="location-editor__name-display">
                <span>{{ name }}</span>
                <button type="button" aria-label="Edit Location name" @click="beginNameEdit">
                  <IconEmbedded name="edit-pencil_2-5" :size="17" />
                </button>
              </div>
              <label v-else class="location-editor__field location-editor__field--grow" aria-label="Location name">
                <input
                  ref="nameInput"
                  v-model="name"
                  class="global-text-input"
                  maxlength="150"
                  placeholder="Business or place name"
                  @focus="isEditingName = true"
                  @blur="finishNameEdit"
                  @keydown.enter.prevent="finishNameEdit"
                />
              </label>
              <small v-if="selectedPlace?.address" class="location-editor__place-address">{{ selectedPlace.address }}</small>
              <a v-if="selectedPlace?.website" class="location-editor__url-link" :href="selectedPlace.website" target="_blank" rel="noopener noreferrer" @click.stop>
                {{ stripUrlProtocol(selectedPlace.website) }}
                <IconEmbedded name="external-link_2-5" :size="14" color="#667085" />
              </a>
            </div>
            <button v-if="selectedPlace" type="button" class="location-editor__fb-change" @click="changePlace">Change</button>
          </div>

          <p v-if="isDiscoveringSocials || discoveryMessage" class="location-editor__status-row location-editor__discovery-status">
            <LoadingIcon v-if="isDiscoveringSocials" :size="18" />
            {{ isDiscoveringSocials ? "Finding social accounts..." : discoveryMessage }}
          </p>

          <label class="location-editor__field location-editor__social-row" style="margin-top: 18px !important">
            <img class="location-editor__social-logo" src="/images/social-logo-instagram.svg" alt="Instagram" />
            <div class="location-editor__social-body">
              <div class="location-editor__handle-input">
                <span>@</span>
                <input v-model="instagramHandle" type="text" placeholder="username" @input="onInstagramInput" @blur="handleInstagramBlur" />
                <LoadingIcon v-if="isCheckingInstagram" class="location-editor__inline-loader" :size="18" />
              </div>
              <div v-if="instagramProfileUrl || instagramStatusMessage" class="location-editor__url-row">
                <a v-if="instagramProfileUrl" class="location-editor__url-link" :href="instagramProfileUrl" target="_blank" rel="noopener noreferrer" @click.stop>
                  {{ instagramDisplayUrl }}
                  <IconEmbedded name="external-link_2-5" :size="14" color="#667085" />
                </a>
                <span
                  v-if="instagramStatusMessage"
                  class="location-editor__status-row"
                  :class="[
                    instagramState === 'valid' ? 'location-editor__valid' : '',
                    instagramState === 'invalid' ? 'location-editor__instagram-guidance' : '',
                    instagramState === 'error' ? 'global-error-string' : '',
                  ]"
                >
                  {{ instagramStatusMessage }}
                </span>
              </div>
            </div>
          </label>

          <div class="location-editor__field location-editor__field--facebook location-editor__social-row">
            <img class="location-editor__social-logo" src="/images/social-logo-facebook.svg" alt="Facebook" />
            <div class="location-editor__social-body">
              <template v-if="!selectedFacebookName">
                <div class="location-editor__fb-tabs" role="tablist" aria-label="Facebook Page input mode">
                  <button
                    type="button"
                    role="tab"
                    :aria-selected="facebookMode === 'search'"
                    class="location-editor__fb-tab"
                    :class="{ 'location-editor__fb-tab--active': facebookMode === 'search' }"
                    @click="setFacebookMode('search')"
                  >
                    Search
                  </button>
                  <button
                    type="button"
                    role="tab"
                    :aria-selected="facebookMode === 'manual'"
                    class="location-editor__fb-tab"
                    :class="{ 'location-editor__fb-tab--active': facebookMode === 'manual' }"
                    @click="setFacebookMode('manual')"
                  >
                    Manual
                  </button>
                </div>

                <div v-if="facebookMode === 'search'" class="location-editor__fb-panel">
                  <input v-model="facebookQuery" class="location-editor__fb-search-input" type="text" placeholder="coming soon..." disabled @input="queueFacebookSearch" />
                  <div v-if="isSearchingFacebook" class="location-editor__fb-status"><LoadingIcon :size="18" /> Searching...</div>
                  <div v-for="page in facebookResults" :key="page.pageId" class="location-editor__fb-result">
                    <div class="location-editor__fb-result-info">
                      <span class="location-editor__fb-name">{{ page.name }}</span>
                      <span v-if="deriveFacebookVanity(page.url)" class="location-editor__fb-handle">{{ deriveFacebookVanity(page.url) }}</span>
                      <small v-if="page.address">{{ page.address }}</small>
                    </div>
                    <SrpButton size="small" color="gray" fill="outlined" @click="selectFacebookPage(page)">Select</SrpButton>
                  </div>
                </div>

                <template v-else>
                  <input
                    v-model="facebookManualInput"
                    class="global-text-input"
                    type="text"
                    placeholder="Paste a Facebook URL, handle, or Page ID"
                    @keydown.enter.prevent="resolveFacebookManualInput"
                    @blur="resolveFacebookManualInput"
                  />
                  <div v-if="isSearchingFacebook" class="location-editor__fb-status location-editor__fb-status--bare"><LoadingIcon :size="18" /> Resolving...</div>
                </template>
                <p v-if="facebookErrorMessage" class="location-editor__status-row global-error-string location-editor__fb-error">{{ facebookErrorMessage }}</p>
              </template>

              <template v-else>
                <div class="location-editor__fb-selected">
                  <span class="location-editor__fb-selected-name">{{ selectedFacebookName }}</span>
                  <button type="button" class="location-editor__fb-change" @click="changeFacebookPage">Change</button>
                </div>
                <div v-if="facebookDisplayUrl || isFacebookVerified" class="location-editor__url-row location-editor__url-row--fb">
                  <a v-if="facebookDisplayUrl" class="location-editor__url-link" :href="facebookProfileUrl || undefined" target="_blank" rel="noopener noreferrer" @click.stop>
                    {{ facebookDisplayUrl }}
                    <IconEmbedded name="external-link_2-5" :size="14" color="#667085" />
                  </a>
                  <span v-if="isFacebookVerified" class="location-editor__status-row location-editor__valid">✓ Page found</span>
                </div>
              </template>
            </div>
          </div>

          <label class="location-editor__field location-editor__social-row">
            <img class="location-editor__social-logo" src="/images/social-logo-tiktok.svg" alt="TikTok" />
            <div class="location-editor__social-body">
              <div class="location-editor__handle-input">
                <span>@</span>
                <input v-model="tikTokHandle" type="text" placeholder="username" @input="onTikTokInput" />
              </div>
              <div v-if="tikTokProfileUrl" class="location-editor__url-row">
                <a class="location-editor__url-link" :href="tikTokProfileUrl" target="_blank" rel="noopener noreferrer" @click.stop>
                  {{ tikTokDisplayUrl }}
                  <IconEmbedded name="external-link_2-5" :size="14" color="#667085" />
                </a>
              </div>
            </div>
          </label>

          <NoteWithIcon v-if="duplicateNameError" size="tiny" color="red" style="margin-top: 20px">
            <template #text>{{ duplicateNameError }}</template>
          </NoteWithIcon>
          <NoteWithIcon v-if="duplicatePlaceWarning" size="tiny" color="yellow" :style="{ marginTop: duplicateNameError ? '10px' : '20px' }">
            <template #text>{{ duplicatePlaceWarning }}</template>
          </NoteWithIcon>

          <p v-if="errorMessage" class="global-error-string">{{ errorMessage }}</p>
        </template>
      </div>
    </template>
    <template #footer="{ closeModal }">
      <div class="location-editor__footer">
        <SrpButton color="gray" fill="outlined" :isDisabled="isSaving" @click.stop="closeModal()">Cancel</SrpButton>
        <SrpButton :isDisabled="isSaving || !canSave" @click.stop="save">
          <template #icon><LoadingIcon v-if="isSaving" :size="18" /><IconEmbedded v-else name="save_2-5" :size="18" /></template>
          Save
        </SrpButton>
      </div>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import LocationAvatar from "./LocationAvatar.vue";
import SocialLocationsRepo from "@repos/SocialLocationsRepo";
import SocialFieldInputCleaner, { buildFacebookPageUrl, buildInstagramProfileUrl, buildTikTokProfileUrl } from "@logic/SocialFieldInputCleaner";
import { extractAddressParts } from "@logic/GooglePlaceConverter";
import AddressHelper from "@helpers/AddressHelper";
import { getGlobalRemoteLogger } from "@helpers/RemoteLogger";
import type { FacebookPageSearchResult, LocationData, SocialDiscoveryFailMode, WebsiteSocialDiscoveryResult } from "@contracts/socialLocations";

const props = withDefaults(defineProps<{ isVisible: boolean; customerId: string; location?: LocationData | null }>(), { location: null });
const emit = defineEmits<{
  (e: "update:isVisible", value: boolean): void;
  (e: "saved", location: LocationData): void;
}>();

interface SelectedPlaceInfo {
  placeId: string;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  website: string | null;
  latitude: number | null;
  longitude: number | null;
}

const name = ref("");
const nameInput = ref<HTMLInputElement | null>(null);
const placeSearchInput = ref<{ $el?: HTMLInputElement } | null>(null);
const selectedPlace = ref<SelectedPlaceInfo | null>(null);
const placeChangedThisSession = ref(false);
const isManualMode = ref(false);
const isDiscoveringSocials = ref(false);
const discoveryMessage = ref("");
/** Set when website discovery ran this session; only then do we persist FailMode on save. */
const discoveryFailModeTracked = ref(false);
const failMode = ref<SocialDiscoveryFailMode | null>(null);
/** Website-discovery Facebook handle kept when auto-resolve fails so Slack can still report it. */
const unverifiedFacebookHandle = ref<string | null>(null);
const existingLocations = ref<LocationData[]>([]);
const duplicateNameError = ref("");
const duplicatePlaceWarning = ref("");
const isEditingName = ref(false);
const instagramHandle = ref("");
const tikTokHandle = ref("");
const instagramState = ref<"unchecked" | "valid" | "invalid" | "error">("unchecked");
const instagramValidatedAt = ref<string | null>(null);
const originalInstagramHandle = ref("");
const originalInstagramValidatedAt = ref<string | null>(null);
const previewIcon = ref<string | null>(null);
const iconId = ref<string | null>(null);
const isCheckingInstagram = ref(false);
const facebookMode = ref<"search" | "manual">("manual");
const facebookQuery = ref("");
const facebookManualInput = ref("");
const facebookResults = ref<FacebookPageSearchResult[]>([]);
const selectedFacebookToken = ref<string | null>(null);
const selectedFacebookPage = ref<FacebookPageSearchResult | null>(null);
const selectedFacebookName = ref("");
const clearExistingFacebook = ref(false);
const isFacebookVerified = ref(false);
const isSearchingFacebook = ref(false);
const isSaving = ref(false);
const errorMessage = ref("");
const facebookErrorMessage = ref("");
const lastCheckedInstagramHandle = ref("");
let facebookTimer: ReturnType<typeof setTimeout> | null = null;
let facebookSearchSequence = 0;
let instagramValidationSequence = 0;
let placeDiscoverySequence = 0;

watch(
  () => [props.isVisible, props.location?.id] as const,
  ([visible]) => {
    if (!visible) return;
    const location = props.location;
    name.value = location?.name ?? "";
    selectedPlace.value = location?.googlePlaceId
      ? {
          placeId: location.googlePlaceId,
          name: location.name,
          address: location.address ?? null,
          city: location.city ?? null,
          state: location.state ?? null,
          country: location.country ?? null,
          website: location.website ?? null,
          latitude: location.latitude ?? null,
          longitude: location.longitude ?? null,
        }
      : null;
    placeChangedThisSession.value = false;
    placeDiscoverySequence++;
    isDiscoveringSocials.value = false;
    discoveryMessage.value = "";
    discoveryFailModeTracked.value = false;
    failMode.value = null;
    unverifiedFacebookHandle.value = null;
    existingLocations.value = [];
    duplicateNameError.value = "";
    duplicatePlaceWarning.value = "";
    originalInstagramHandle.value = normalizeInstagramHandle(location?.socialSet?.instagramHandle ?? "");
    originalInstagramValidatedAt.value = location?.socialSet?.instagramValidatedAt ?? null;
    instagramHandle.value = originalInstagramHandle.value;
    tikTokHandle.value = normalizeTikTokHandle(location?.socialSet?.tikTokHandle ?? "");
    instagramValidatedAt.value = originalInstagramValidatedAt.value;
    instagramState.value = instagramValidatedAt.value ? "valid" : "unchecked";
    lastCheckedInstagramHandle.value = originalInstagramHandle.value;
    previewIcon.value = location?.icon ?? null;
    iconId.value = location?.icon ?? null;
    selectedFacebookToken.value = null;
    selectedFacebookPage.value = null;
    selectedFacebookName.value = location?.socialSet?.facebookPageName ?? "";
    clearExistingFacebook.value = false;
    isFacebookVerified.value = location?.socialSet?.facebookLoadType === "Scrape";
    facebookMode.value = "manual";
    facebookQuery.value = "";
    facebookManualInput.value = "";
    facebookResults.value = [];
    instagramValidationSequence++;
    isCheckingInstagram.value = false;
    errorMessage.value = "";
    facebookErrorMessage.value = "";
    isManualMode.value = false;
    isEditingName.value = false;

    void loadExistingLocations();

    if (!location) {
      nextTick(() => {
        placeSearchInput.value?.$el?.focus();
      });
    }
  },
  { immediate: true }
);

watch([name, selectedPlace], () => refreshDuplicateChecks());

const hasIdentity = computed(() => Boolean(instagramHandle.value.trim() || selectedFacebookName.value || tikTokHandle.value.trim()));
// Mirrors what the save will actually persist: a place picked now, or the stored one we're leaving alone.
const hasPlaceIdentity = computed(() =>
  Boolean(selectedPlace.value?.placeId || selectedPlace.value?.website || (!placeChangedThisSession.value && (props.location?.googlePlaceId || props.location?.website)))
);
/** Everything below the search stays hidden until they pick a place, choose Manual Mode, or open an existing Location. */
const showDetailFields = computed(() => Boolean(props.location || selectedPlace.value || isManualMode.value));
const isNameEditMode = computed(() => !name.value.trim() || isEditingName.value);
const isInstagramValidForSave = computed(
  () => !instagramHandle.value.trim() || isOriginalInstagramHandle(instagramHandle.value) || (instagramState.value === "valid" && Boolean(instagramValidatedAt.value))
);
const canSave = computed(() => Boolean(name.value.trim() && (hasIdentity.value || hasPlaceIdentity.value) && isInstagramValidForSave.value && !duplicateNameError.value));
// Only link out to a handle we've confirmed. While someone is retyping it, the link would point
// at an account that may not exist, so it stays hidden until the new handle checks out.
const instagramProfileUrl = computed(() =>
  instagramState.value === "valid" || isOriginalInstagramHandle(instagramHandle.value) ? buildInstagramProfileUrl(normalizeInstagramHandle(instagramHandle.value)) : ""
);
const instagramDisplayUrl = computed(() => stripUrlProtocol(instagramProfileUrl.value));
// TikTok has no validation endpoint, so show a preview link whenever a handle is present.
const tikTokProfileUrl = computed(() => {
  const handle = normalizeTikTokHandle(tikTokHandle.value);
  return handle ? buildTikTokProfileUrl(handle) : "";
});
const tikTokDisplayUrl = computed(() => stripUrlProtocol(tikTokProfileUrl.value));
const facebookProfileUrl = computed(() => {
  if (selectedFacebookPage.value) {
    return buildFacebookPageUrl(selectedFacebookPage.value.url, selectedFacebookPage.value.pageId);
  }
  if (clearExistingFacebook.value || !selectedFacebookName.value) return "";
  const socialSet = props.location?.socialSet;
  return buildFacebookPageUrl(socialSet?.facebookPageUrl, socialSet?.facebookPageId);
});
const facebookDisplayUrl = computed(() => {
  const vanity = deriveFacebookVanity(facebookProfileUrl.value);
  return vanity ? `facebook.com/${vanity}` : "";
});
const instagramStatusMessage = computed(() => {
  if (instagramState.value === "valid") return "✓ Account found";
  if (instagramState.value === "invalid") return "Couldn't find that Instagram account. Check the handle and try again.";
  if (instagramState.value === "error") return "Couldn't verify that Instagram account right now. Please try again.";
  return "";
});

function stripUrlProtocol(url: string): string {
  return url.replace(/^https?:\/\//i, "");
}

function mapPlaceToInfo(place: globalThis.google.maps.places.PlaceResult): SelectedPlaceInfo {
  const addressParts = extractAddressParts(place);
  return {
    placeId: place.place_id ?? "",
    name: place.name ?? "",
    address: place.formatted_address ? AddressHelper.removeCountryAndZipCode(place.formatted_address) : null,
    city: addressParts.city ?? null,
    state: addressParts.state ?? null,
    country: addressParts.country ?? null,
    website: place.website ?? null,
    latitude: place.geometry?.location?.lat?.() ?? null,
    longitude: place.geometry?.location?.lng?.() ?? null,
  };
}

async function onGooglePlaceSelected(place: globalThis.google.maps.places.PlaceResult) {
  // Fires with no place_id when someone types free text without picking a suggestion.
  if (!place?.place_id) return;
  getGlobalRemoteLogger().info(`LocationEditor googlePlaceSelected: ${place.name} ${place.place_id}`);

  const placeInfo = mapPlaceToInfo(place);
  selectedPlace.value = placeInfo;
  placeChangedThisSession.value = true;
  isManualMode.value = false;
  if (!name.value.trim()) name.value = placeInfo.name;

  const sequence = ++placeDiscoverySequence;
  discoveryMessage.value = "";

  if (!placeInfo.website) {
    discoveryMessage.value = "No website found for this location - add social accounts below.";
    return;
  }
  await runSocialDiscovery(sequence, placeInfo.website);
}

function onAutocompleteError(error: any) {
  getGlobalRemoteLogger().error(`LocationEditor Autocomplete Error in Google Maps: ${error}`);
}

function changePlace() {
  selectedPlace.value = null;
  // Handles/name/icon are intentionally kept, so keep the fields on screen rather than hiding
  // what they already entered behind the search box.
  isManualMode.value = true;
  // Cancels applying any in-flight discovery.
  placeDiscoverySequence++;
  isDiscoveringSocials.value = false;
  discoveryMessage.value = "";
}

async function loadExistingLocations() {
  try {
    existingLocations.value = await SocialLocationsRepo.list(props.customerId);
    refreshDuplicateChecks();
  } catch {
    // Non-blocking: duplicate checks are a nicety; don't lock the user out if the list fails.
    existingLocations.value = [];
    duplicateNameError.value = "";
    duplicatePlaceWarning.value = "";
  }
}

function refreshDuplicateChecks() {
  const editingId = props.location?.id;
  const activeLocations = existingLocations.value.filter(l => !l.deletedDate && l.id !== editingId);
  const trimmedName = name.value.trim();
  const placeId = selectedPlace.value?.placeId;

  const nameDuplicate = trimmedName
    ? activeLocations.find(l => l.name.trim().toLowerCase() === trimmedName.toLowerCase())
    : undefined;
  const placeDuplicate = placeId ? activeLocations.find(l => l.googlePlaceId === placeId) : undefined;

  duplicateNameError.value = nameDuplicate ? `You already have this location saved: "${nameDuplicate.name}"` : "";
  // Same location matching both: only the red name error. Different locations: show both.
  const showPlaceWarning = Boolean(placeDuplicate && (!nameDuplicate || placeDuplicate.id !== nameDuplicate.id));
  duplicatePlaceWarning.value = showPlaceWarning ? `You already have a Location for this Google Place: "${placeDuplicate!.name}"` : "";
}

async function runSocialDiscovery(sequence: number, websiteUrl: string) {
  isDiscoveringSocials.value = true;
  try {
    const result = await SocialLocationsRepo.discoverSocialsFromWebsite(props.customerId, websiteUrl);
    if (sequence !== placeDiscoverySequence) return;
    await applyDiscoveryResults(sequence, result);
  } catch {
    if (sequence !== placeDiscoverySequence) return;
    discoveryMessage.value = "Couldn't find social accounts from the website - add them below.";
  } finally {
    if (sequence === placeDiscoverySequence) isDiscoveringSocials.value = false;
  }
}

/** Only fills fields that are still empty, so anything the customer already entered wins. */
async function applyDiscoveryResults(sequence: number, result: WebsiteSocialDiscoveryResult) {
  const tasks: Promise<void>[] = [];
  const found: string[] = [];

  discoveryFailModeTracked.value = true;
  failMode.value = result.siteUnavailable ? "SiteUnavailable" : result.websiteIsSpa ? "WebsiteIsSpa" : null;

  const instagramCandidate = topCandidate(result.instagram);
  if (instagramCandidate && !instagramHandle.value.trim()) {
    const handle = normalizeInstagramHandle(instagramCandidate.handle || instagramCandidate.url);
    if (handle) {
      instagramHandle.value = handle;
      found.push("Instagram");
      tasks.push(checkInstagram(handle, ++instagramValidationSequence));
    }
  }

  const facebookCandidate = topCandidate(result.facebook);
  if (facebookCandidate && !selectedFacebookName.value) {
    found.push("Facebook");
    unverifiedFacebookHandle.value = facebookCandidateHandle(facebookCandidate);
    tasks.push(autoResolveFacebook(facebookCandidate.url || facebookCandidate.handle));
  }

  const tikTokCandidate = topCandidate(result.tiktok);
  if (tikTokCandidate && !tikTokHandle.value.trim()) {
    const handle = normalizeTikTokHandle(tikTokCandidate.handle || tikTokCandidate.url);
    if (handle) {
      tikTokHandle.value = handle;
      found.push("TikTok");
    }
  }

  await Promise.allSettled(tasks);
  if (sequence !== placeDiscoverySequence) return;
  discoveryMessage.value = found.length ? `Found ${formatFoundList(found)} - review below.` : "Couldn't find social accounts - please add below.";

  // Website apple-touch-icon only when IG/FB did not already set an icon (never overwrite).
  const iconUrls = (result.websiteIconUrls?.length ? result.websiteIconUrls : result.websiteIconUrl ? [result.websiteIconUrl] : []).filter(Boolean);
  if (!iconId.value && iconUrls.length) {
    for (const iconUrl of iconUrls) {
      if (iconId.value) break;
      try {
        const importedIconId = await SocialLocationsRepo.importWebsiteIcon(props.customerId, iconUrl);
        if (sequence !== placeDiscoverySequence) return;
        if (importedIconId && !iconId.value) {
          iconId.value = importedIconId;
          previewIcon.value = importedIconId;
          break;
        }
      } catch {
        // Non-blocking: try the next candidate; initials remain if none import.
      }
    }
  }
}

async function autoResolveFacebook(input: string) {
  const sequence = ++facebookSearchSequence;
  try {
    const resolved = await SocialLocationsRepo.resolveFacebookPage(props.customerId, normalizeFacebookResolveInput(input), true);
    // Bail if the customer picked their own Page while this was running.
    if (sequence !== facebookSearchSequence || selectedFacebookName.value) return;
    selectFacebookPage(resolved);
    if (discoveryFailModeTracked.value && failMode.value === "CantVerifyFacebook") failMode.value = null;
  } catch {
    // Leave it blank so the customer can enter it manually. No error banner for the automatic path.
    if (discoveryFailModeTracked.value) failMode.value = "CantVerifyFacebook";
  }
}

function topCandidate(candidates: WebsiteSocialDiscoveryResult["instagram"]) {
  return candidates?.slice().sort((a, b) => a.rank - b.rank)[0] ?? null;
}

function facebookCandidateHandle(candidate: NonNullable<ReturnType<typeof topCandidate>>): string | null {
  const handle = (candidate.handle || deriveFacebookVanity(candidate.url) || candidate.url || "").trim().replace(/^@+/, "");
  return handle || null;
}

function formatFoundList(found: string[]): string {
  if (found.length === 1) return found[0];
  return `${found.slice(0, -1).join(", ")} and ${found[found.length - 1]}`;
}

function isOriginalInstagramHandle(value: string): boolean {
  return Boolean(props.location && originalInstagramHandle.value && normalizeInstagramHandle(value) === originalInstagramHandle.value);
}

function restoreOriginalInstagramValidation() {
  instagramState.value = originalInstagramValidatedAt.value ? "valid" : "unchecked";
  instagramValidatedAt.value = originalInstagramValidatedAt.value;
  lastCheckedInstagramHandle.value = originalInstagramHandle.value;
}

function onInstagramInput() {
  instagramValidationSequence++;
  isCheckingInstagram.value = false;
  instagramHandle.value = normalizeInstagramHandle(instagramHandle.value);
  if (isOriginalInstagramHandle(instagramHandle.value)) {
    restoreOriginalInstagramValidation();
    return;
  }
  instagramState.value = "unchecked";
  instagramValidatedAt.value = null;
  lastCheckedInstagramHandle.value = "";
}

function onTikTokInput() {
  tikTokHandle.value = normalizeTikTokHandle(tikTokHandle.value);
}

async function handleInstagramBlur() {
  const normalizedHandle = normalizeInstagramHandle(instagramHandle.value);
  if (!normalizedHandle) {
    instagramHandle.value = "";
    instagramState.value = "unchecked";
    instagramValidatedAt.value = null;
    lastCheckedInstagramHandle.value = "";
    return;
  }
  instagramHandle.value = normalizedHandle;
  if (isOriginalInstagramHandle(normalizedHandle)) {
    restoreOriginalInstagramValidation();
    return;
  }
  if (normalizedHandle === lastCheckedInstagramHandle.value) return;
  const sequence = ++instagramValidationSequence;
  await checkInstagram(normalizedHandle, sequence);
}

async function beginNameEdit() {
  isEditingName.value = true;
  await nextTick();
  nameInput.value?.focus();
  nameInput.value?.select();
}

function finishNameEdit() {
  isEditingName.value = false;
}

function clearIcon() {
  previewIcon.value = null;
  iconId.value = null;
}

async function checkInstagram(handle: string, sequence: number) {
  isCheckingInstagram.value = true;
  errorMessage.value = "";
  try {
    const result = await SocialLocationsRepo.validateInstagram(props.customerId, handle, true);
    if (sequence !== instagramValidationSequence || normalizeInstagramHandle(instagramHandle.value) !== handle) return;
    instagramState.value = result.isValid ? "valid" : "invalid";
    lastCheckedInstagramHandle.value = handle;
    if (result.isValid) {
      instagramHandle.value = result.username;
      lastCheckedInstagramHandle.value = normalizeInstagramHandle(result.username);
      instagramValidatedAt.value = result.validatedAt ?? new Date().toISOString();
      // Don't overwrite a name/icon we already have (ex. from the Google Place the customer picked).
      if (result.name && !name.value.trim()) name.value = result.name;
      if (result.iconId) {
        iconId.value = result.iconId;
        previewIcon.value = result.iconId;
      }
    } else {
      instagramValidatedAt.value = null;
    }
  } catch {
    if (sequence !== instagramValidationSequence || normalizeInstagramHandle(instagramHandle.value) !== handle) return;
    instagramState.value = "error";
    instagramValidatedAt.value = null;
  } finally {
    if (sequence === instagramValidationSequence) isCheckingInstagram.value = false;
  }
}

function normalizeInstagramHandle(value: string): string {
  return SocialFieldInputCleaner.cleanInstagramHandle(value).trim().replace(/^@+/, "").toLowerCase();
}

function normalizeTikTokHandle(value: string): string {
  return SocialFieldInputCleaner.cleanTikTokHandle(value).trim();
}

function setFacebookMode(mode: "search" | "manual") {
  if (facebookMode.value === mode) return;
  facebookMode.value = mode;
  if (facebookTimer) clearTimeout(facebookTimer);
  facebookSearchSequence++;
  facebookResults.value = [];
  isSearchingFacebook.value = false;
  facebookErrorMessage.value = "";
}

function queueFacebookSearch() {
  if (facebookTimer) clearTimeout(facebookTimer);
  const sequence = ++facebookSearchSequence;
  facebookResults.value = [];
  isSearchingFacebook.value = false;
  facebookErrorMessage.value = "";
  const input = facebookQuery.value.trim();
  if (input.length < 2) return;
  facebookTimer = setTimeout(() => runFacebookLookup(sequence, input, "search"), 350);
}

async function resolveFacebookManualInput() {
  if (facebookTimer) clearTimeout(facebookTimer);
  const input = normalizeFacebookResolveInput(facebookManualInput.value.trim());
  if (!input) return;

  const inputKind = classifyFacebookInput(input);
  if (inputKind !== "resolve") {
    facebookErrorMessage.value = facebookResolveErrorMessage;
    return;
  }

  const sequence = ++facebookSearchSequence;
  facebookResults.value = [];
  await runFacebookLookup(sequence, input, "resolve");
}

async function runFacebookLookup(sequence: number, input: string, inputKind: "search" | "resolve") {
  isSearchingFacebook.value = true;
  facebookErrorMessage.value = "";
  try {
    if (inputKind === "resolve") {
      const result = await SocialLocationsRepo.resolveFacebookPage(props.customerId, input, true);
      if (sequence === facebookSearchSequence) {
        selectFacebookPage(result);
        isFacebookVerified.value = true;
      }
    } else {
      const results = await SocialLocationsRepo.searchFacebookPages(props.customerId, input);
      if (sequence === facebookSearchSequence) facebookResults.value = results;
    }
  } catch (error: any) {
    if (sequence === facebookSearchSequence) facebookErrorMessage.value = inputKind === "resolve" ? facebookResolveErrorMessage : getErrorMessage(error);
  } finally {
    if (sequence === facebookSearchSequence) isSearchingFacebook.value = false;
  }
}

const facebookResolveErrorMessage = "Couldn't resolve that Facebook Page. Try its handle, url, Page Id, or search by name.";

function classifyFacebookInput(input: string): "search" | "resolve" | "partial-url" | "invalid-url" {
  if (/^[0-9]{5,25}$/.test(input)) return "resolve";
  if (/^[0-9]+$/.test(input)) return input.length < 5 ? "partial-url" : "invalid-url";
  if (isSupportedFacebookUrl(input)) return "resolve";
  if (isFacebookHandle(input)) return "resolve";

  const looksLikeFacebookUrl = /^(?:https?:\/\/)?(?:(?:www|m|web)\.)?(?:facebook\.com|fb\.com)(?:[/?#]|$)/i.test(input);
  if (looksLikeFacebookUrl) return "partial-url";
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(input)) return "invalid-url";
  return "search";
}

const facebookAllowedHosts = new Set(["facebook.com", "www.facebook.com", "m.facebook.com", "web.facebook.com", "fb.com", "www.fb.com"]);
const facebookReservedPaths = new Set([
  "about",
  "ads",
  "business",
  "events",
  "groups",
  "help",
  "home.php",
  "login",
  "marketplace",
  "messages",
  "people",
  "photo",
  "photo.php",
  "photos",
  "plugins",
  "privacy",
  "profile.php",
  "reel",
  "reels",
  "share",
  "stories",
  "story.php",
  "watch",
  "permalink.php",
  "pages",
  "pg",
]);

function isFacebookHandle(input: string): boolean {
  const handle = input.replace(/^@+/, "").trim();
  if (!/^[A-Za-z0-9.]{2,75}$/.test(handle) || /^[0-9]+$/.test(handle)) return false;
  return !facebookReservedPaths.has(handle.toLowerCase()) && !facebookAllowedHosts.has(handle.toLowerCase());
}

function isSupportedFacebookUrl(input: string): boolean {
  const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(input) ? input : `https://${input}`;
  try {
    const url = new URL(candidate);
    return (url.protocol === "https:" || url.protocol === "http:") && facebookAllowedHosts.has(url.hostname.toLowerCase()) && url.pathname !== "/";
  } catch {
    return false;
  }
}

function isFacebookPageIdentitySegment(segment: string): boolean {
  if (facebookReservedPaths.has(segment.toLowerCase())) return false;
  return /^[0-9]{5,25}$/.test(segment) || /^[A-Za-z0-9.]{2,75}$/.test(segment);
}

/** Normalize pasteable Facebook URLs into a handle, Page ID, or cleaned vanity URL. */
function normalizeFacebookResolveInput(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return "";
  const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(candidate);
    if (!facebookAllowedHosts.has(url.hostname.toLowerCase())) return trimmed;
    const segments = url.pathname
      .split("/")
      .filter(Boolean)
      .map(s => decodeURIComponent(s));
    if (segments.length === 0) return trimmed;

    if (/^profile\.php$/i.test(segments[0])) {
      const id = url.searchParams.get("id")?.trim() ?? "";
      if (/^[0-9]{5,25}$/.test(id)) return id;
      return trimmed;
    }
    if (segments.length >= 3 && /^people$/i.test(segments[0]) && /^[0-9]{5,25}$/.test(segments[2])) return segments[2];
    if (segments.length >= 2 && /^pages$/i.test(segments[0]) && /^[0-9]{5,25}$/.test(segments[segments.length - 1])) {
      return segments[segments.length - 1];
    }
    if (segments.length >= 2 && /^pg$/i.test(segments[0]) && isFacebookPageIdentitySegment(segments[1])) return segments[1];
    if (isFacebookPageIdentitySegment(segments[0])) return segments[0];
  } catch {
    return trimmed;
  }
  return trimmed;
}

function deriveFacebookVanity(url?: string | null): string {
  const input = (url || "").trim();
  if (!input) return "";
  const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(input) ? input : `https://${input}`;
  try {
    const parsed = new URL(candidate);
    if (!/(^|\.)(facebook|fb)\.com$/i.test(parsed.hostname)) return "";
    const segments = parsed.pathname.split("/").filter(Boolean);
    if (segments.length === 0) return "";
    const segment = decodeURIComponent(segments[0]);
    if (/^[0-9]+$/.test(segment) || facebookReservedPaths.has(segment.toLowerCase())) return "";
    if (!/^[A-Za-z0-9.]{2,75}$/.test(segment)) return "";
    return segment;
  } catch {
    return "";
  }
}

function selectFacebookPage(page: FacebookPageSearchResult) {
  selectedFacebookToken.value = page.selectionToken ?? null;
  selectedFacebookPage.value = page;
  selectedFacebookName.value = page.name;
  unverifiedFacebookHandle.value = null;
  clearExistingFacebook.value = false;
  isFacebookVerified.value = page.loadType === "Scrape";
  if (!name.value.trim() && page.name) name.value = page.name;
  if (!iconId.value && page.iconId) {
    iconId.value = page.iconId;
    previewIcon.value = page.iconId;
  }
  facebookResults.value = [];
  facebookQuery.value = "";
  facebookManualInput.value = "";
}

function clearFacebookPage() {
  selectedFacebookToken.value = null;
  selectedFacebookPage.value = null;
  selectedFacebookName.value = "";
  isFacebookVerified.value = false;
  clearExistingFacebook.value = Boolean(props.location?.socialSet?.facebookPageId);
}

function changeFacebookPage() {
  clearFacebookPage();
  facebookMode.value = "manual";
  facebookQuery.value = "";
  facebookManualInput.value = "";
  facebookResults.value = [];
  facebookErrorMessage.value = "";
}

async function save() {
  if (isSaving.value || !canSave.value) return;
  isSaving.value = true;
  errorMessage.value = "";
  try {
    const request = {
      eTag: props.location?._etag,
      name: name.value.trim(),
      instagramHandle: normalizeInstagramHandle(instagramHandle.value) || null,
      instagramValidatedAt: instagramValidatedAt.value,
      iconId: iconId.value,
      facebookPageSelectionToken: selectedFacebookToken.value,
      facebookPageSelection: selectedFacebookPage.value
        ? {
            pageId: selectedFacebookPage.value.pageId,
            pageName: selectedFacebookPage.value.name,
            pageUrl: selectedFacebookPage.value.url ?? null,
            address: selectedFacebookPage.value.address ?? null,
            latitude: selectedFacebookPage.value.latitude ?? null,
            longitude: selectedFacebookPage.value.longitude ?? null,
            validatedAt: selectedFacebookPage.value.validatedAt ?? null,
            loadType: selectedFacebookPage.value.loadType ?? null,
          }
        : null,
      clearFacebookPage: clearExistingFacebook.value,
      tikTokHandle: normalizeTikTokHandle(tikTokHandle.value) || null,
      // Only sent when a place was picked this session. Otherwise the server keeps what it already has.
      googlePlaceId: placeChangedThisSession.value ? selectedPlace.value?.placeId ?? null : null,
      website: placeChangedThisSession.value ? selectedPlace.value?.website ?? null : null,
      address: placeChangedThisSession.value ? selectedPlace.value?.address ?? null : null,
      city: placeChangedThisSession.value ? selectedPlace.value?.city ?? null : null,
      state: placeChangedThisSession.value ? selectedPlace.value?.state ?? null : null,
      country: placeChangedThisSession.value ? selectedPlace.value?.country ?? null : null,
      latitude: placeChangedThisSession.value ? selectedPlace.value?.latitude ?? null : null,
      longitude: placeChangedThisSession.value ? selectedPlace.value?.longitude ?? null : null,
      updateFailMode: discoveryFailModeTracked.value,
      failMode: discoveryFailModeTracked.value ? failMode.value : undefined,
      isManualMode: isManualMode.value,
      unverifiedFacebookHandle: unverifiedFacebookHandle.value || null,
    };
    const saved = props.location ? await SocialLocationsRepo.update(props.customerId, props.location.id, request) : await SocialLocationsRepo.create(props.customerId, request);
    emit("update:isVisible", false);
    emit("saved", saved);
  } catch (error: any) {
    errorMessage.value = getErrorMessage(error);
  } finally {
    isSaving.value = false;
  }
}

function getErrorMessage(error: any): string {
  const value = error?.response?.data;
  if (typeof value === "string") return value;
  return value?.detail ?? value?.title ?? error?.message ?? "Unable to save this Location.";
}
</script>

<style scoped lang="scss">
.location-editor {
  display: grid;
  gap: 12px;
}
.location-editor__header {
  display: grid;
  gap: 4px;
}
.location-editor__header h2,
.location-editor__header p {
  margin: 0;
}
.location-editor__header p {
  // color: #667085;
  font-size: 15px;
}
.location-editor__identity,
.location-editor__footer {
  display: flex;
  align-items: center;
  gap: 12px;
}
.location-editor__avatar-wrap {
  position: relative;
  flex: 0 0 auto;
  // padding: 4px 4px 0 0;
}
.location-editor__clear-icon {
  position: absolute;
  top: 0;
  right: 0;
  display: inline-grid;
  place-items: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 1px solid #98a2b3;
  border-radius: 999px;
  background: #fff;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.12);
}
.location-editor__clear-icon:hover {
  color: #101828;
  border-color: #667085;
  background: #f9fafb;
}
.location-editor__name-display {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  color: #000;
  font-size: 18px;
  font-weight: 600;
}
.location-editor__name-display span {
  // overflow: hidden;
  // text-overflow: ellipsis;
  // white-space: nowrap;
}
.location-editor__name-display button {
  display: inline-grid;
  place-items: center;
  flex: 0 0 auto;
  padding: 3px;
  border: 0;
  background: transparent;
  color: #667085;
  cursor: pointer;
}
.location-editor__field {
  display: grid;
  gap: 7px;
  font-size: 14px;
  font-weight: 600;
}
.location-editor__field--grow {
  flex: 1;
}
// The Facebook panel pieces manage their own spacing (the tabs sit flush against the panel).
.location-editor__field--facebook .location-editor__social-body {
  gap: 0;
}
.location-editor__field--facebook .location-editor__social-logo {
  margin-top: 14px;
}
.location-editor__identity-body {
  display: grid;
  gap: 2px;
  flex: 1;
  min-width: 0;
}
.location-editor__place-address {
  color: #667085;
  font-size: 13px;
  font-weight: 400;
}
.location-editor__identity-body .location-editor__url-link {
  justify-self: start;
  font-size: 14px;
}
.location-editor__social-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
// Profile url and its status sit on one line, wrapping when the status message is long.
.location-editor__url-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 12px;
  min-width: 0;
}
// The Facebook panel sets gap 0, so this row needs its own breathing room under the box.
.location-editor__url-row--fb {
  margin-top: 7px;
}
.location-editor__social-logo {
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  margin-top: 8px;
}
.location-editor__social-body {
  display: grid;
  gap: 7px;
  flex: 1;
  min-width: 0;
}
.location-editor__discovery-status {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  color: #667085;
  font-weight: 400;
}
.location-editor__handle-input {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  border: 1px solid #cbd0d7;
  border-radius: 6px;
  overflow: hidden;
  background: white;
}
.location-editor__url-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #667085;
  font-size: 14px;
  font-weight: 400;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.location-editor__url-link:hover {
  text-decoration: underline;
}
.location-editor__handle-input span {
  padding: 10px 12px;
  border-right: 1px solid #d8dce2;
  color: #667085;
}
.location-editor__handle-input input {
  flex: 1;
  min-width: 0;
  padding: 10px;
  border: 0;
  outline: 0;
}
.location-editor__inline-loader {
  flex: 0 0 auto;
  margin-right: 10px;
  color: #667085;
}
.location-editor__status-row {
  margin: 0;
  font-size: 14px;
  line-height: 18px;
}
.location-editor__valid {
  color: #21883f;
  font-weight: 600;
}
.location-editor__instagram-guidance {
  color: #ec573b;
  font-weight: 600;
}
.location-editor__fb-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 0;
  padding: 4px;
  border-radius: 8px;
  background: #f2f4f7;
}
.location-editor__fb-tab {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: #667085;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.12s ease,
    color 0.12s ease;
}
.location-editor__fb-tab:hover:not(.location-editor__fb-tab--active) {
  color: #344054;
}
.location-editor__fb-tab--active {
  border-color: #d8dce2;
  background: #fff;
  color: #101828;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.08);
}
.location-editor__fb-panel {
  border: 1px solid #d8dce2;
  border-radius: 3px;
  background: #fff;
  overflow: hidden;
}
.location-editor__fb-search-input {
  width: 100%;
  padding: 11px 14px;
  border: 0;
  outline: 0;
  font: inherit;
  font-size: 14px;
  font-weight: 400;
}
.location-editor__fb-search-input::placeholder {
  color: #98a2b3;
}
.location-editor__fb-status {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-top: 1px solid #e7e9ed;
  color: #667085;
  font-size: 14px;
  font-weight: 400;
}
.location-editor__fb-status--bare {
  padding: 0;
  border-top: 0;
}
.location-editor__fb-result {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  border-top: 1px solid #e7e9ed;
}
.location-editor__fb-result-info {
  display: grid;
  gap: 2px;
  min-width: 0;
}
.location-editor__fb-name {
  color: #101828;
  font-size: 15px;
  font-weight: 600;
}
.location-editor__fb-handle {
  color: #667085;
  font-size: 14px;
  font-weight: 400;
}
.location-editor__fb-result-info small {
  color: #667085;
  font-size: 13px;
  font-weight: 400;
}
.location-editor__fb-selected {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid #d8dce2;
  border-radius: 8px;
  background: #fff;
}
.location-editor__fb-selected-name {
  min-width: 0;
  color: #101828e0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.location-editor__fb-change {
  flex: 0 0 auto;
  padding: 4px;
  border: 0;
  background: transparent;
  color: #667085;
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}
.location-editor__fb-change:hover {
  color: #101828;
}
.location-editor__fb-error {
  margin: 7px 0 0;
}
.location-editor__footer {
  justify-content: flex-end;
}
</style>

<style lang="scss">
// Google appends the autocomplete dropdown to the body at z-index 1000, which is below this modal.
.pac-container {
  z-index: 1300;
}
</style>
